import React, { useState } from "react";
import "./ContactForm.css";

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    hidden: "", // honeypot (must remain empty)
  });

  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Validation function
  const validateForm = () => {
    const errors = [];

    // Name validation: required, 2+ characters, letters and spaces only
    if (!form.name) {
      errors.push("Navn er påkrævet.");
    } else if (form.name.length < 2) {
      errors.push("Navn skal være mindst 2 tegn langt.");
    } else if (!/^[A-Za-z\s]+$/.test(form.name)) {
      errors.push("Navn må kun indeholde bogstaver og mellemrum.");
    }

    // Email validation: required, valid email format
    if (!form.email) {
      errors.push("E-mail er påkrævet.");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.push("Indtast venligst en gyldig e-mailadresse.");
    }

    // Phone validation: optional, but if provided, must match Danish phone number format
    if (form.phone && !/^\+?45\s*-?\d{2}\s*-?\d{2}\s*-?\d{2}\s*-?\d{2}$|^\d{2}\s*-?\d{2}\s*-?\d{2}\s*-?\d{2}$/.test(form.phone)) {
      errors.push("Indtast venligst et gyldigt dansk telefonnummer (f.eks. +4534567890 eller 34567890).");
    }

    // Company validation: optional, max 100 characters
    if (form.company && form.company.length > 100) {
      errors.push("Virksomhedsnavn må ikke overstige 100 tegn.");
    }

    // Message validation: required, max 500 characters
    if (!form.message) {
      errors.push("Besked er påkrævet.");
    } else if (form.message.length > 500) {
      errors.push("Besked må ikke overstige 500 tegn.");
    }

    // Honeypot: must be empty
    if (form.hidden) {
      errors.push("Formularindsendelse blokeret på grund af spamregistrering.");
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Perform validation
    const errors = validateForm();
    if (errors.length > 0) {
      setErrorMsg(errors.join(" "));
      setStatus("error");
      return;
    }

    // Honeypot check (redundant due to validation, but kept for clarity)
    if (form.hidden) {
      setStatus("sent");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      let body = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        message: form.message,
      };

      // Handle reCAPTCHA if required
      if (showCaptcha) {
        if (!window.grecaptcha) {
          throw new Error("reCAPTCHA script not loaded");
        }
        const token = await window.grecaptcha.execute(
          "DIN_SITE_KEY", // Erstat med din site key
          { action: "submit" }
        );
        body.captchaToken = token;
      }

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.requireCaptcha) {
        setShowCaptcha(true);
        setStatus("idle");
        return;
      }

      if (!res.ok) throw new Error(data.error || `Server returnerede ${res.status}`);

      setStatus("sent");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
        hidden: "",
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
      setErrorMsg("Der opstod en fejl. Prøv igen senere.");
    }
  };

  return (
    <div className="form-container">
      <section className="form-card" aria-labelledby="contact-title">
        <form className="form-body" onSubmit={handleSubmit} noValidate>
          {/* Honeypot */}
          <input
            type="text"
            name="hidden"
            value={form.hidden}
            onChange={onChange}
            style={{ display: "none" }}
            tabIndex="-1"
            autoComplete="off"
          />

          <label htmlFor="name">Navn</label>
          <input
            className="input"
            id="name"
            name="name"
            type="text"
            placeholder="Dit navn"
            value={form.name}
            onChange={onChange}
            required
          />

          <label htmlFor="email">E-mail</label>
          <input
            className="input"
            id="email"
            name="email"
            type="email"
            placeholder="Din e-mail"
            value={form.email}
            onChange={onChange}
            required
          />

          <label htmlFor="phone">Telefon (valgfri)</label>
          <input
            className="input"
            id="phone"
            name="phone"
            type="tel"
            placeholder="Dit telefonnummer (f.eks. +4534567890)"
            value={form.phone}
            onChange={onChange}
          />

          <label htmlFor="company">Virksomhed (valgfri)</label>
          <input
            className="input"
            id="company"
            name="company"
            type="text"
            placeholder="Virksomhedens navn"
            value={form.company}
            onChange={onChange}
          />

          <label htmlFor="message">Besked</label>
          <textarea
            className="input"
            id="message"
            name="message"
            rows="5"
            placeholder="Skriv din besked her"
            value={form.message}
            onChange={onChange}
            required
          />

          {/* reCAPTCHA container */}
          {showCaptcha && (
            <div
              className="g-recaptcha"
              data-sitekey="6LdyJrgrAAAAAD1cBf43Q7_4rCndoVZ2ROpo_VpG" // Erstat med din site key
              data-size="invisible"
            ></div>
          )}

          <button
            type="submit"
            className="submit-btn"
            disabled={status === "sending"}
          >
            {status === "sending" ? "Sender…" : "Send besked"}
          </button>

          {status === "sent" && (
            <p className="success">
              Tak for din henvendelse! Jeg ser frem til at høre fra dig.
            </p>
          )}
          {status === "error" && <p className="error">{errorMsg}</p>}
        </form>
      </section>
    </div>
  );
}